import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Tag, Calendar, MapPin } from 'lucide-react';
import { StatusPill } from 'src/components/ui/status-pill';
import { useToast } from 'src/hooks/use-toast';
import { PartnerService } from 'src/services/api/PartnerService';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';

const partnerTypeLabel = (type) => {
  if (type === 1 || type === '1') return 'Partner';
  if (type === 2 || type === '2') return 'Institution';
  return 'Unknown';
};

const DetailRow = ({ label, value, mono = false }) => (
  <div>
    <p className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-muted-foreground mb-1">
      {label}
    </p>
    {mono ? (
      <p className="font-mono text-[11px] font-[550] text-muted-foreground tracking-[.03em]">
        {value || 'N/A'}
      </p>
    ) : (
      <p className="text-[13px] font-[500] text-foreground-strong">
        {value || 'N/A'}
      </p>
    )}
  </div>
);

const PartnerDetail = () => {
  const { orgId } = useParams();
  const { toast } = useToast();
  const [partnerData, setPartnerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPartnerDetail = async () => {
      try {
        setIsLoading(true);
        const response = await PartnerService.getPartnerDetail(orgId);
        if (response) {
          setPartnerData(response);
        } else {
          throw new Error('Failed to load partner details');
        }
      } catch (err) {
        console.error('Error fetching partner detail:', err);
        setError(err.message || 'Failed to load partner details');
        toast({
          message: err.message || 'Failed to load partner details',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (orgId) {
      fetchPartnerDetail();
    }
  }, [orgId, toast]);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Partner Details" />
        <PageContainer className="flex items-center justify-center">
          <p className="text-[13px] text-muted-foreground">
            Loading partner details…
          </p>
        </PageContainer>
      </div>
    );
  }

  if (error || !partnerData) {
    return (
      <div>
        <PageHeader title="Partner Details" />
        <PageContainer>
          <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] p-6 text-center">
            <p className="text-[13px] text-[#DC2626]">
              {error || 'Failed to load partner details'}
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  const isActive = Boolean(partnerData.is_active);
  const partnerType = partnerTypeLabel(partnerData.partner_type);
  const isPartnerType =
    partnerData.partner_type === 1 || partnerData.partner_type === '1';

  return (
    <div>
      <PageHeader title="Partner Details" />

      <PageContainer>
        <div className="max-w-[820px] mx-auto space-y-4">
          {/* Header card */}
          <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-[10px] bg-brand-bg-subtle flex items-center justify-center flex-shrink-0">
                  <Building2
                    className="w-5 h-5 text-primary"
                    strokeWidth={1.8}
                  />
                </div>
                <div>
                  <h2 className="text-[17px] font-bold text-foreground-strong tracking-[-0.02em] leading-snug">
                    {partnerData.name}
                  </h2>
                  <div className="flex items-center gap-2.5 mt-2 flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-md border text-[12px] font-[550] ${
                        isPartnerType
                          ? 'bg-brand-bg-subtle text-[#2168B2] border-[rgba(32, 104, 178,.15)]'
                          : 'bg-[#F3F0FF] text-[#7C3AED] border-[rgba(124,58,237,.15)]'
                      }`}
                    >
                      <Tag className="w-3 h-3" strokeWidth={2} />
                      {partnerType}
                    </span>
                    {partnerData.created_at && (
                      <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5" strokeWidth={1.8} />
                        {new Date(partnerData.created_at).toLocaleDateString(
                          'en-IN',
                          { day: '2-digit', month: 'short', year: 'numeric' }
                        )}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <StatusPill
                name={isActive ? 'Active' : 'Inactive'}
                textColor={isActive ? 'emerald-600' : 'red-600'}
                borderColor={isActive ? 'emerald-200' : 'red-200'}
                bgColor={isActive ? 'emerald-50' : 'red-50'}
              />
            </div>
          </div>

          {/* Address card */}
          <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden">
            <div className="px-5 py-3.5 flex items-center gap-2 border-b border-border bg-card">
              <MapPin className="w-4 h-4 text-primary" strokeWidth={1.8} />
              <span className="text-[13px] font-[620] text-foreground-strong tracking-[-0.01em]">
                Address Information
              </span>
            </div>

            <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-5">
              <div className="col-span-2">
                <DetailRow
                  label="Address Line 1"
                  value={partnerData.address_line_1}
                />
              </div>
              <div className="col-span-2">
                <DetailRow label="Street" value={partnerData.street} />
              </div>
              <DetailRow
                label="City"
                value={partnerData.city || partnerData.city_id?.toString()}
              />
              <DetailRow
                label="State"
                value={partnerData.state || partnerData.state_id?.toString()}
              />
              <DetailRow label="ZIP Code" value={partnerData.zip_code} />
              <DetailRow label="GSTIN" value={partnerData.gstin} mono />
            </div>
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default PartnerDetail;
